import {
  AddressInput,
  Button,
  ButtonsWrapper,
  Form,
  Modal,
  PhoneNumberInput,
  SelectInput,
  TextAreaInput,
  TextInput,
  Typography,
} from '@thinknet-gmbh/platform-common-ui';
import { SalutationsEnum } from '@thinknet-gmbh/platform-common';
import { useTranslation } from 'react-i18next';
import NumberFormat from 'react-number-format';

import { useCreateCustomer } from 'src/hooks/customers/create-customer.hook';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { ContactPersonsList } from 'src/pages/customers/components/contact-persons-form-list';

import { ModalContent, AddressTitle } from './create-customer-modal.styles';

const CreateCustomerModal = ({ onClose }: { onClose: () => void }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation(['common', 'customers']);

  const currentCompanyId = useGetCurrentCompanyId();

  const createCustomer = useCreateCustomer({ onSuccess: onClose });

  const onFinish = (values: any) => {
    const {
      address: { city, zipCode, country, street },
    } = values;
    createCustomer({
      variables: {
        payload: {
          ...values,
          email: values.email ? values.email : null,
          address: {
            city: city || '',
            zipCode: zipCode || 0,
            street: street || '',
            country: country || '',
          },
          companyId: currentCompanyId,
        },
      },
    });
  };

  return (
    <Modal
      isOpened
      onClose={onClose}
      title={t('customers:buttons.createCustomer')}
      modalWidth={644}
    >
      <Form layout="vertical" form={form} onFinish={onFinish}>
        <ModalContent>
          <div>
            <Form.Item
              name="salutation"
              label={t('customers:labels.salutation')}
              rules={[
                {
                  required: true,
                  message: t('customers:error.salutationRequired'),
                },
              ]}
            >
              <SelectInput placeholder={t('customers:labels.salutations.mr')}>
                <SelectInput.Option value={SalutationsEnum.MR}>
                  {t('customers:labels.salutations.mr')}
                </SelectInput.Option>
                <SelectInput.Option value={SalutationsEnum.MS}>
                  {t('customers:labels.salutations.ms')}
                </SelectInput.Option>
                <SelectInput.Option value={SalutationsEnum.COMPANY}>
                  {t('customers:labels.salutations.company')}
                </SelectInput.Option>
              </SelectInput>
            </Form.Item>
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const salutation = getFieldValue('salutation');

                return (
                  <Form.Item
                    name="companyName"
                    label={t('customers:labels.companyName')}
                    rules={[
                      {
                        required: !salutation || salutation === SalutationsEnum.COMPANY,
                        message: t('settings:company.exceptions.requiredCompanyName'),
                      },
                    ]}
                  >
                    <TextInput placeholder="Billinger" maxLength={50} />
                  </Form.Item>
                );
              }}
            </Form.Item>
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const salutation = getFieldValue('salutation');

                return (
                  <Form.Item
                    name="customerName"
                    label={t('customers:labels.customerName')}
                    rules={[
                      {
                        required: salutation !== SalutationsEnum.COMPANY,
                        message: t('customers:error.customerNameRequired'),
                      },
                    ]}
                  >
                    <TextInput placeholder="Savannah Nguyen" maxLength={50} />
                  </Form.Item>
                );
              }}
            </Form.Item>
            <Form.Item
              name="email"
              label={t('common:labels.email')}
              rules={[{ type: 'email', message: t('common:validation.error.incorrectFormat') }]}
            >
              <TextInput placeholder="angelinasteffans@gmail.com" maxLength={50} />
            </Form.Item>
            <Form.Item name="phoneNumber" label={t('common:labels.phoneNumber')}>
              <NumberFormat customInput={TextInput} placeholder="9-13-123" format="#-##-###" />
            </Form.Item>
            <Form.Item name="mobilePhoneNumber" label={t('customers:labels.mobilePhoneNumber')}>
              <PhoneNumberInput placeholder="+49 176 384 15749" />
            </Form.Item>
            <Form.Item name="notes" label={t('customers:labels.notes')}>
              <TextAreaInput
                maxLength={500}
                showCount
                placeholder={t('customers:labels.notesPlaceholder')}
              />
            </Form.Item>
          </div>
          <AddressTitle>
            <Typography weight="bold">{t('common:labels.businessAddress')}</Typography>
            <AddressInput t={t} parentField="address" notRequired />
            <ContactPersonsList t={t} />
          </AddressTitle>
        </ModalContent>
        <ButtonsWrapper marginTop={24}>
          <Button htmlType="submit" type="primary">
            {t('common:buttons.save')}
          </Button>
          <Button onClick={onClose}>{t('common:buttons.cancel')}</Button>
        </ButtonsWrapper>
      </Form>
    </Modal>
  );
};

export default CreateCustomerModal;
